import { FormEvent, useState } from 'react';

import Button from '../common/Button';
import Input from '../common/Input';
import { SectionCard } from '../common/PageHeader';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { api, errorMessage } from '../../services/api';
import { compact, hasErrors, serverFieldErrors, v } from '../../utils/validation';

export default function PasswordTab() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [errors, setErrors] = useState<Partial<Record<keyof typeof form, string>>>({});
  const [saving, setSaving] = useState(false);

  function setField(key: keyof typeof form, value: string) {
    setForm((p) => ({ ...p, [key]: value }));
    setErrors((p) => (p[key] ? { ...p, [key]: undefined } : p));
  }

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    const e = compact({
      currentPassword: form.currentPassword ? undefined : 'Current password is required',
      newPassword:
        v.password(form.newPassword, 'New password') ??
        (form.newPassword === form.currentPassword ? 'New password must be different from the current one' : undefined),
      confirm: !form.confirm ? 'Confirm the new password' : form.confirm !== form.newPassword ? 'Passwords do not match' : undefined,
    });
    setErrors(e);
    if (hasErrors(e)) return;

    setSaving(true);
    try {
      await api.post('/auth/change-password', { currentPassword: form.currentPassword, newPassword: form.newPassword });
      showToast('Password changed', 'success');
      setForm({ currentPassword: '', newPassword: '', confirm: '' });
    } catch (err) {
      // "Current password is incorrect" belongs under that field.
      const message = errorMessage(err, 'Failed to change password');
      if (/current password/i.test(message)) setErrors({ currentPassword: message });
      else setErrors(serverFieldErrors(err, ['currentPassword', 'newPassword'] as const));
      showToast(message, 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectionCard title="Change Password" className="max-w-md">
      <p className="text-sm text-ink-500 mb-4">Signed in as <span className="font-semibold text-ink-800">{user?.email}</span></p>
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <Input label="Current Password" type="password" required value={form.currentPassword} maxLength={72} autoComplete="current-password"
          onChange={(e) => setField('currentPassword', e.target.value)} error={errors.currentPassword} />
        <Input label="New Password" type="password" required value={form.newPassword} maxLength={72} autoComplete="new-password"
          onChange={(e) => setField('newPassword', e.target.value)} error={errors.newPassword}
          helperText="8-72 characters with at least one letter and one number" />
        <Input label="Confirm New Password" type="password" required value={form.confirm} maxLength={72} autoComplete="new-password"
          onChange={(e) => setField('confirm', e.target.value)} error={errors.confirm} />
        <Button type="submit" variant="primary" icon="fa-key" disabled={saving}>{saving ? 'Saving…' : 'Change Password'}</Button>
      </form>
    </SectionCard>
  );
}
